import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useCancelOffer, type WorkOrderOffer } from '@/hooks/useWorkOrderOffers';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';

interface CancelOfferDialogProps {
  open: boolean;
  workOrderId: string;
  offer: WorkOrderOffer;
  onClose: () => void;
}

export function CancelOfferDialog({
  open,
  workOrderId,
  offer,
  onClose,
}: CancelOfferDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const cancelOffer = useCancelOffer();
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setSubmitError(null);
  }, [open]);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !cancelOffer.isPending;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      await cancelOffer.mutateAsync({
        workOrderId,
        offerId: offer.id,
        reason: trimmed,
      });
      notify.success(t('offers.cancelledTitle'), t('offers.cancelledDesc'));
      onClose();
    } catch (error) {
      const messageText = describeApiError(error, t, {
        codePrefix: 'offers.errorCode',
        fallback: t('offers.cancelFailed'),
      });
      setSubmitError(messageText);
      notify.error(t('offers.cancelFailed'), messageText);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={cancelOffer.isPending ? undefined : onClose}
      mobileTitle={t('offers.cancelTitle')}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('offers.cancelTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <DialogContentText>{t('offers.cancelMessage')}</DialogContentText>
        <TextField
          autoFocus
          label={t('offers.cancelReason')}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          multiline
          minRows={2}
          fullWidth
          required
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cancelOffer.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={!canSubmit}
          startIcon={cancelOffer.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('offers.cancelConfirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
